import { useState, useRef, useLayoutEffect, useCallback } from "react";
import { select as d3Select } from 'd3-selection';
import { interpolate as d3Interpolate } from 'd3-interpolate';
// Constants
import * as constants from './constants';

// An attr value can either be a plain value (animated via d3's default
// interpolator) or a shape spec: { generator, params }. For shape specs,
// `params` is a plain object of numbers (e.g. arc angles/radii) and
// `generator` turns resolved params into the actual attr value (e.g. a path
// 'd' string). Animating this way (tweening the underlying numbers, then
// re-running the generator each tick) avoids d3's default string
// interpolation, which breaks on paths containing arc flags (0/1 values
// that must never pass through a fraction).
const is_shape_spec = (value) => value && typeof value === 'object' && typeof value.generator === 'function';
const resolve_attr = (value) => is_shape_spec(value) ? value.generator(value.params) : value;

// A recorded position is only evidence that the element is still moving if it
// was recorded recently. Anything older means the transition that produced it
// has since finished (or was a one off seeding call) and the element is sitting
// still. Generous enough to survive dropped frames - this diagram tweens a path
// per node and per ribbon at once, so frames can run well over their budget -
// while staying short next to the shortest animation on screen.
const VELOCITY_STALE_MS = 500;
// Bounds on the starting slope, in units of "whole journeys per duration". 3 is
// the largest value that keeps the ease monotonic, so above it the element
// would sail past its target and come back. A small negative slope is allowed
// so that reversing direction still carries a little momentum.
const MIN_SLOPE = -1;
const MAX_SLOPE = 3;
// Below this the element was barely moving, so there is no momentum worth
// preserving and the configured ease is left to do its normal job.
const MIN_MEANINGFUL_SLOPE = 0.01;

// d3's object interpolator returns the same object every tick, overwriting its
// values in place, so anything held on to across ticks has to be copied out.
const deep_copy = (params) => {
  const copy = {};
  for (let key in params) {
    copy[key] = (typeof params[key] === 'number') ? params[key] : deep_copy(params[key]);
  }
  return copy;
};

// Params are nested plain objects of numbers, so walking a pair of them means
// recursing. Produces a same-shaped object of per-param rates of change.
const params_velocity = (from, to, elapsed) => {
  const velocity = {};
  for (let key in to) {
    velocity[key] = (typeof to[key] === 'number')
      ? (to[key] - (from?.[key] ?? to[key])) / elapsed
      : params_velocity(from?.[key], to[key], elapsed);
  }
  return velocity;
};

// How fast the element is already travelling along the path this transition is
// about to take, as a fraction of the new journey per millisecond. Params are
// multi dimensional but an ease is scalar, so project the velocity vector onto
// the direction of travel - motion across the new path contributes nothing,
// motion along it contributes in full.
const project_velocity = (velocity, from, to, acc = {dot : 0, distance_sq : 0}) => {
  for (let key in to) {
    if (typeof to[key] === 'number') {
      const delta = to[key] - (from?.[key] ?? to[key]);
      acc.dot += (velocity?.[key] ?? 0) * delta;
      acc.distance_sq += delta * delta;
    } else {
      project_velocity(velocity?.[key], from?.[key], to[key], acc);
    }
  }
  return acc;
};

// The unique cubic with e(0)=0, e(1)=1, e'(0)=slope and e'(1)=0: it leaves at
// the speed the interrupted transition was travelling and still settles gently
// on to the target. At slope 0 it is exactly smoothstep, i.e. an ease-in-out.
const velocity_ease = (slope) => (t) =>
  ((slope - 2) * t + (3 - 2 * slope)) * t * t + slope * t;

const useAnimated = ({
  attrs,
  duration,
  ease,
  deps,
  on_start,
  on_stop,
  run_on_first_render = false
}) => {
  // Local state
  const resolved_attrs = {};
  for (let attr in attrs) resolved_attrs[attr] = resolve_attr(attrs[attr]);
  const [local_attrs, _update_attrs] = useState(resolved_attrs);
  // Create ref
  let ref = useRef(null);
  // Whether this is the first render. Use state here rather than ref since
  // dev mode (StrictMode) can cause the first render to be called twice,
  // which would accidentally cause the layout effect to run twice - once
  // with first_render true and once with first_render false. Using state
  // ensures that the layout effect has the same behaviuor on these two
  // back-to-back render calls.
  const [first_render, _set_first_render] = useState(true);
  // Where each shape-spec attr currently is on screen, keyed by attr name.
  // Updated on every tween tick (not just at the end) so that a transition
  // interrupted part way through leaves behind the params actually being
  // displayed, and the replacement transition can pick up from there.
  const prev_params_ref = useRef({});
  // The tick before that one ({params, time}), and the rate of change derived
  // from the pair. Used to work out how fast an interrupted attr was moving.
  const last_tick_ref = useRef({});
  const velocity_ref = useRef({});

  // Components sometimes drive a hook-managed attr themselves - typically a
  // mount animation that runs once, off different params to the ones this hook
  // knows about. Such an animation must report where it has got to, otherwise
  // interrupting it leaves this hook tweening from a position the element never
  // reached. Call this synchronously when seeding the attr, and again on every
  // tick of the tween.
  const record_params = useCallback((attr, params) => {
    const now = performance.now();
    const last = last_tick_ref.current[attr];
    // Must be a copy: the caller's object is very likely one d3 is about to
    // overwrite on the next tick, which would silently turn the difference
    // below into zero.
    const snapshot = deep_copy(params);
    // Record the rate of change whatever the gap between ticks - over a long
    // gap it is simply an average over that gap, which is still the best
    // estimate available. Judging whether it is recent enough to act on is left
    // to the point of use, which is the only place that knows when 'now' is.
    velocity_ref.current[attr] = (last && now > last.time)
      ? params_velocity(last.params, snapshot, now - last.time)
      : null;
    last_tick_ref.current[attr] = {params : snapshot, time : now};
    prev_params_ref.current[attr] = snapshot;
  }, []);

  // Animate
  useLayoutEffect(() => {
    if (!ref.current) return;

    // Capture where each shape attr is tweening from. Don't move the baseline
    // on to the new params here - the element hasn't got there yet, and if this
    // transition is interrupted it never will.
    const shape_froms = {};
    for (let attr in attrs) {
      const value = attrs[attr];
      if (is_shape_spec(value)) {
        shape_froms[attr] = prev_params_ref.current[attr] ?? value.params;
        prev_params_ref.current[attr] = shape_froms[attr];
      }
    }

    if ((first_render && !run_on_first_render) || (duration === 0)) {
      // No animation, so the new params are applied straight away
      for (let attr in attrs) {
        if (is_shape_spec(attrs[attr])) prev_params_ref.current[attr] = attrs[attr].params;
      }
      _set_first_render(false);
      _update_attrs(resolved_attrs);
      return;
    }

    // Work out whether this transition is taking over from one still in flight,
    // and if so how fast the element was travelling towards where it is now
    // headed. Do this before building the transition, while the velocity
    // recorded by the interrupted one is still fresh.
    // One transition carries one ease, so every shape attr on it contributes to
    // a single projection: attrs that were moving add to the numerator, and
    // attrs sitting still still add their share of the distance, damping the
    // slope by however much of the journey they account for.
    const now = performance.now();
    const projection = {dot : 0, distance_sq : 0};
    let moving = false;
    for (let attr in attrs) {
      const value = attrs[attr];
      if (!is_shape_spec(value)) continue;
      const last = last_tick_ref.current[attr];
      const is_moving = velocity_ref.current[attr] && last && (now - last.time < VELOCITY_STALE_MS);
      if (is_moving) moving = true;
      project_velocity(is_moving ? velocity_ref.current[attr] : null, shape_froms[attr], value.params, projection);
    }
    // dot/distance_sq is journeys per millisecond; scaling by the duration puts
    // it in the ease's own units, where 1 is the whole journey.
    const slope = (moving && projection.distance_sq)
      ? Math.min(MAX_SLOPE, Math.max(MIN_SLOPE, (projection.dot / projection.distance_sq) * duration))
      : 0;

    // Do animation
    const element = d3Select(ref.current);
    const transition = element.transition().duration(duration);
    for (let attr in attrs) {
      const value = attrs[attr];
      if (is_shape_spec(value)) {
        const interpolator = d3Interpolate(shape_froms[attr], value.params);
        transition.attrTween(attr, () => (t) => {
          const params = interpolator(t);
          record_params(attr, params);
          return value.generator(params);
        });
      } else {
        transition.attr(attr, value);
      }
    }
    if (moving && Math.abs(slope) > MIN_MEANINGFUL_SLOPE) {
      // Picking up from a moving element: leave at the speed it is already
      // travelling rather than easing in from a standstill it is not at.
      transition.ease(velocity_ease(slope));
    } else if (ease) {
      transition.ease(ease);
    } else {
      transition.ease(constants.DEFAULT_EASE);
    }
    on_start?.();

    // After transition ended, update local state to final values
    transition.on('end', () => {
      if (!ref.current) return;
      _update_attrs(resolved_attrs);
      on_stop?.();
    });

    // Cleanup by ending transitions
    return () => {
      element.interrupt();
      on_stop?.();
    }
  }, deps);

  return [ref, local_attrs, record_params];
}

export default useAnimated;
